import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * Entry point for the Putty alpha app (alpha.putty.me).
 *
 * A deliberately thin shell around the one screen this app ships:
 * `<HomeScreen />` (home + collection + block-detail-with-annotations). It
 * wires only what that screen needs to talk to the real Putty API:
 *
 *   - QueryClientProvider — TanStack Query, used by the screen + `useAccountMe`.
 *   - AuthProvider        — Supabase phone/email OTP. The collection endpoints
 *                           are user-scoped, so the app is auth-gated.
 *   - ApiSetup            — hands the auth context's `getAccessToken` to the
 *                           API client so every fetch carries a Bearer token.
 *   - AuthGate            — loading / login / app, keyed off auth status.
 *
 * No router (the screen owns its own home/collection/detail view state), no
 * service worker, no analytics — see README.md for the rationale.
 */
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/auth/auth-context";
import { setAuthTokenProvider } from "@/api/base-query-api";
import { DevApp, isDevPath } from "@/dev/dev-app";
import { HomeScreen } from "@/screens/home/home-screen";
import { LoginScreen } from "@/screens/login/login-screen";
import "./index.scss";
const queryClient = new QueryClient();
/**
 * Wires the API client's auth-token provider to the auth context's
 * `getAccessToken`. Without this, every API fetch lands without an
 * `Authorization` header and the server returns 401. Must render inside
 * `<AuthProvider>`.
 */
const ApiSetup = ({ children }) => {
    const { getAccessToken } = useAuth();
    useEffect(() => {
        setAuthTokenProvider(getAccessToken);
    }, [getAccessToken]);
    return _jsx(_Fragment, { children: children });
};
/**
 * Gates on auth status:
 *   - uninitialized → blank loading shell (auth is resolving the session)
 *   - logged_out    → login screen
 *   - logged_in     → the app
 */
const AuthGate = () => {
    const { status } = useAuth();
    if (status === "uninitialized") {
        return _jsx("div", { className: "alpha-loading" });
    }
    if (status === "logged_out") {
        return (_jsx("div", { className: "alpha-auth-shell", children: _jsx(LoginScreen, {}) }));
    }
    return _jsx(HomeScreen, {});
};
if (process.env.NODE_ENV !== "production") {
    const devLabel = process.env.WORKTREE_NAME || "DEV";
    document.title = `[${devLabel}] ${document.title}`;
}
const container = document.getElementById("root");
if (container) {
    const root = ReactDOM.createRoot(container);
    // Dev design surfaces under `/dev/` skip the providers + auth gate entirely —
    // they render static, mock-data copies of components in isolation.
    if (isDevPath(window.location.pathname)) {
        root.render(_jsx(React.StrictMode, { children: _jsx(DevApp, {}) }));
    }
    else {
        root.render(_jsx(React.StrictMode, { children: _jsx(QueryClientProvider, { client: queryClient, children: _jsx(AuthProvider, { children: _jsx(ApiSetup, { children: _jsx(AuthGate, {}) }) }) }) }));
    }
}
